'use client';

import * as DS from '@aurorasolar/ds';
import { x } from '@xstyled/styled-components';
import Link from 'next/link';

import { privacyPolicyUrl } from '@/app/components';

export const TermsConditionsBody = () => {
  return (
    <>
      <x.h1 py={10} text="d3">
        Terms of Use for Aurora Marketplace
      </x.h1>
      <DS.Divider />
      <DS.Flex maxW={DS.BorealisTheme.screens.md} mx="auto" px="4" py="8">
        <p>
          <em>Last Updated: July 22, 2026</em>
        </p>

        <p>
          Welcome to Aurora! We provide an online platform (the “Service”) that connects homeowners (“You,” “Your,”
          “User”) with independent third-party installers (“Installers”) for solar installation.
        </p>
        <p>
          PLEASE READ THESE TERMS AND CONDITIONS (“Terms”) CAREFULLY. THIS IS A BINDING LEGAL AGREEMENT BETWEEN YOU AND
          AURORA SOLAR INC (“AURORA,” “WE,” “US,” OR “OUR”). BY ACCESSING, REGISTERING FOR, OR USING THE SERVICES, YOU:
          (A) ACKNOWLEDGE THAT YOU HAVE READ AND UNDERSTOOD THESE Terms AND THE INCORPORATED DOCUMENTS; (B) REPRESENT
          THAT YOU ARE OF LEGAL AGE TO ENTER INTO A BINDING AGREEMENT; AND (C) AGREE THAT YOU ARE LEGALLY BOUND BY THEM.
          IF YOU DO NOT AGREE, DO NOT ACCESS OR USE THE SERVICES.
        </p>
        <p>
          These Terms, together with our <Link href={privacyPolicyUrl}>Privacy Policy</Link>, govern Your use of the
          Aurora Marketplace Services as a homeowner, including any managed sales support Aurora provides on behalf of
          certain Installers as described in Section 3.5, and You acknowledge that You have read and agree to be bound
          by both. These Marketplace Terms, not Aurora’s standard{' '}
          <a href="https://aurorasolar.com/terms-of-use/">General Terms</a>, govern your use of the Marketplace as a
          homeowner. If you access the Services on behalf of a business, other agreements apply, but these Terms control
          your Marketplace usage in any conflict. “Services” includes our websites, mobile and web applications,
          widgets, tools, APIs, related support, and any new or enhanced features, including features or products that
          may require additional fees. You acknowledge that Your mobile carrier’s standard charges and data rates apply
          to any access to the Services from a mobile device, and that Your access may be limited by Your mobile
          carrier’s terms and policies or by Your mobile device.
        </p>

        <h3>1. DESCRIPTION OF SERVICES; NATURE OF PLATFORM</h3>

        <p>
          <strong>1.1. Services.</strong> Aurora operates an online platform designed to connect Homeowners interested
          in solar energy systems with independent, third-party Installers. The Services may include: matching You with
          Installers based on information You provide; homeowner validation as described in Section 2; facilitating
          communication between You, Aurora, and Installers; providing sales support on behalf of certain Installers as
          described in Section 3.5; generating preliminary solar designs and estimates; and transferring Your contact
          information and project details (a “Lead”) to one or more Installers serving Your area upon your consent, who
          may contact You directly. Aurora may also communicate with You in connection with the Services. The Services
          may be offered directly through our website or embedded within an Installer’s or partner’s website through an
          Aurora-powered interface; in all cases, Your submission of information through an Aurora-powered interface is
          governed by these Terms, our Privacy Policy, and Aurora’s consent flow.
        </p>

        <p>
          <strong>1.2. Aurora as a Neutral Intermediary.</strong> YOU ACKNOWLEDGE AND AGREE THAT AURORA ACTS SOLELY AS A
          NEUTRAL INTERMEDIARY PLATFORM. AURORA DOES NOT PROVIDE SOLAR INSTALLATION SERVICES, IS NOT A SOLAR INSTALLER,
          AND IS NOT AN AGENT, EMPLOYEE, PARTNER, OR JOINT VENTURER OF ANY INSTALLER. AURORA DOES NOT MANUFACTURE, SELL,
          INSTALL, MAINTAIN, OR WARRANTY ANY SOLAR ENERGY SYSTEM OR COMPONENT, DOES NOT SELL SOLAR ENERGY SYSTEMS ON
          BEHALF OF ANY INSTALLER, AND DOES NOT MANAGE THE SALES PROCESS BETWEEN YOU AND ANY INSTALLER, IN EACH CASE
          EXCEPT AS EXPRESSLY DESCRIBED IN SECTION 3.5 (MANAGED SALES). AURORA IS NOT A LENDER, LOAN BROKER, OR CREDIT
          SERVICES ORGANIZATION AND DOES NOT OFFER, ARRANGE, PROMOTE, OR RECOMMEND FINANCING.
        </p>

        <p>
          <strong>1.3. Availability.</strong> Aurora does not guarantee Installer availability in all locations and may
          determine, in its sole discretion, whether Services are available to You (for example, based on unsupported
          use cases, installer limitations, qualification issues, permitting or HOA restrictions, or non-homeowner
          status; renters are ineligible). Certain features, including the display of financing-related pricing or
          terms, may be modified, limited, or unavailable in certain jurisdictions as required to comply with applicable
          law.
        </p>

        <h3>2. HOMEOWNER VALIDATION; YOUR AUTHORIZATION</h3>

        <p>
          <strong>2.1. Service Checks.</strong> YOU UNDERSTAND, ACKNOWLEDGE, AND AGREE THAT CERTAIN VERIFICATIONS AND
          CHECKS ARE A CONDITION PRECEDENT TO RECEIVING THE SERVICES. To provide the Services, Aurora may perform, or
          have performed, certain verifications (“Service Checks”), which may include utility bill reviews and property
          or ownership verification, and, where applicable for Managed Sales (Section 3.5), soft credit inquiries
          obtained with Your authorization (soft inquiries do not impact Your credit score). Estimates generated through
          the Services, including any financing-related figures, may be based in part on information You self-report
          (such as an estimated credit score range). Aurora does not make credit decisions and will obtain consumer
          report information only with Your authorization and as permitted by applicable law.
        </p>

        <p>
          <strong>2.2. Your Authorization.</strong> BY SUBMITTING YOUR INFORMATION THROUGH ANY MEANS (INCLUDING OUR
          WEBSITE, APPLICATIONS, OR AN AURORA-POWERED INTERFACE EMBEDDED IN AN INSTALLER’S OR PARTNER’S WEBSITE),
          CLICKING ANY BUTTON OR CHECKBOX DENOTING YOUR ACCEPTANCE OF THESE Terms OR YOUR AFFIRMATIVE REQUEST FOR
          SERVICES (SUCH AS “SUBMIT,” “GET MY ESTIMATE,” “I AGREE,” “CONTINUE,” OR SIMILAR PHRASING), OR PROVIDING
          VERBAL CONSENT (FOR EXAMPLE, BY STATING “I ACCEPT,” “YES,” “I AGREE,” “OKAY,” OR WORDS TO SIMILAR EFFECT)
          DURING ANY COMMUNICATION WITH AURORA OR ITS AUTHORIZED REPRESENTATIVES (WHICH MAY BE RECORDED IN ACCORDANCE
          WITH APPLICABLE LAW), YOU AUTHORIZE AURORA AND ITS DESIGNATED THIRD-PARTY AGENTS TO CONDUCT THESE SERVICE
          CHECKS AND USE THE RESULTING INFORMATION to provide and improve the Services, assess Your and Your property’s
          suitability for solar, match You with Installers, and facilitate the preliminary design and estimate process,
          as further described in our <Link href={privacyPolicyUrl}>Privacy Policy</Link>.
        </p>

        <h3>3. INSTALLERS; RELATIONSHIP OF THE PARTIES</h3>

        <p>
          <strong>3.1. Independent Installers.</strong> Installers are independent third-party contractors, not
          employees, agents, or representatives of Aurora. While Aurora performs certain diligence before admitting
          Installers to the platform, AURORA DOES NOT GUARANTEE OR WARRANTY ANY INSTALLER OR THEIR SERVICES, and does
          not independently verify all information provided by Installers or other third parties or guarantee its
          accuracy. Aurora does not control, and is not responsible for, any Installer’s conduct, responsiveness,
          pricing, or performance. Aurora’s vetting, display, ranking, or featuring of any Installer (including any
          badge or designation shown through the Services) reflects Aurora’s diligence and platform criteria only and is
          not a guarantee or warranty of any Installer or their services. You acknowledge that there is an inherent risk
          in engaging with any third party, including the risk of fraud, misrepresentation, or dealing with individuals
          acting under false pretenses. AURORA IS NOT RESPONSIBLE OR LIABLE FOR ANY FRAUDULENT, DECEPTIVE, OR ILLEGAL
          ACTIVITY OF ANY INSTALLER OR OTHER USER OF THE SERVICE.
        </p>

        <p>
          <strong>3.2. No Agency; No Negotiation.</strong> EXCEPT AS EXPRESSLY DESCRIBED IN SECTION 3.5 (MANAGED SALES),
          AURORA DOES NOT ACT ON BEHALF OF, OR REPRESENT, YOU OR ANY INSTALLER, AND DOES NOT NEGOTIATE ANY TERMS OF ANY
          INSTALLATION AGREEMENT. After Your Lead is transferred, You may be contacted by the Installer, Aurora, or
          both, and all decisions regarding pricing, system design, financing, and contract terms are made between You
          and the Installer.
        </p>

        <p>
          <strong>3.3. Your Agreement Is with the Installer.</strong> ANY AGREEMENT FOR THE PURCHASE, INSTALLATION,
          FINANCING, OR SERVICING OF A SOLAR ENERGY SYSTEM IS SOLELY BETWEEN YOU AND YOUR CHOSEN INSTALLER OR, WITH
          RESPECT TO ANY FINANCING, YOUR CHOSEN FINANCIER OR LENDER; AURORA IS NOT AND WILL NOT BE A PARTY TO ANY SUCH
          AGREEMENT AND UNDERTAKES NO RESPONSIBILITY FOR THE TERMS OF ANY SUCH AGREEMENT OR THEIR ENFORCEMENT. The
          Installer is solely responsible for all aspects of the system, including design, engineering, installation,
          performance, maintenance, financing terms, and warranties. You are solely responsible for Your homeowner
          obligations, including compliance with financing and payment terms, property access, and responding to
          requests for information.
        </p>

        <p>
          <strong>3.4. Your Due Diligence.</strong> YOU ARE SOLELY RESPONSIBLE FOR EVALUATING, SELECTING, AND ENGAGING
          ANY INSTALLER. Before signing any contract, You should verify the Installer’s licensing, bonding, and
          insurance; check references, reviews, and complaint histories; obtain and understand detailed written
          contracts before any work or payment; be cautious of large upfront or unconventional payment requests,
          high-pressure tactics, and offers that seem too good to be true; and consult Your own legal, financial, or
          other advisors as needed. To report suspected fraud, You may notify us at{' '}
          <a href="mailto:consumer@aurorasolar.com">consumer@aurorasolar.com</a> (such report creates no liability or
          obligation for Aurora); Your primary recourse is with law enforcement, the applicable licensing board, and the
          state attorney general’s office.
        </p>

        <p>
          <strong>3.5. Managed Sales.</strong> For certain designated Installers (“Managed Sales Installers”), Aurora
          may provide sales support on the Installer’s behalf, and Aurora personnel may act as sales representatives of
          the Installer in connection with Your purchase (“Managed Sales”). Where Aurora provides Managed Sales, Aurora
          will disclose to You, at or before the time Aurora first acts on the Installer’s behalf (including in
          connection with Aurora’s outreach to You), that it is acting as a sales representative of the identified
          Installer. Managed Sales may include managing aspects of the sales and preliminary design process and
          facilitating the transmission of the installation contract from the Installer to You for Your review and
          signature. Aurora may assist in communications regarding the pricing or design features of a potential solar
          energy system, but will not negotiate any other terms of the installation agreement; those terms are required
          by the Installer, and if You request changes to them, Aurora may transfer Your case directly to the Installer
          to continue the process. In all cases: (a) in providing Managed Sales, Aurora acts solely on behalf of the
          Installer and not on Your behalf, and Aurora’s representation of the Installer is limited in scope and does
          not create an agency relationship for any other purpose; (b) any agreement for the purchase, installation,
          financing, or servicing of a solar energy system remains solely between You and the Installer, and Aurora is
          not and will not be a party to any such agreement; (c) the Installer remains solely responsible for the system
          and all aspects of its design, engineering, installation, performance, maintenance, pricing, financing terms,
          and warranties; (d) Aurora may receive compensation from the Installer in connection with Managed Sales, as
          described in Section 16; and (e) except as expressly stated in this Section 3.5 or prohibited by applicable
          law, the disclaimers, acknowledgements, and limitations in these Terms continue to apply. Managed Sales does
          not make Aurora a lender, loan broker, or credit services organization, and Section 5 (Financing) continues to
          apply.
        </p>

        <h3>4. ESTIMATES AND CONCEPTUAL INFORMATION</h3>

        <p>
          <strong>4.1. Estimates Only.</strong> Aurora may provide or facilitate access to preliminary designs,
          specifications, cost estimates, energy production figures, or potential savings calculations (“Conceptual
          Information”) for informational purposes only. ALL FIGURES PRESENTED THROUGH THE SERVICES ARE ESTIMATES, NOT
          QUOTES, OFFERS, OR BINDING PRICES. Final pricing, equipment, design, and financing terms are determined solely
          by the Installer and, where applicable, its financing partners. Conceptual Information is preliminary, subject
          to change, and NOT TO BE USED FOR ENGINEERING, PERMITTING, OR INSTALLATION PURPOSES.
        </p>

        <p>
          <strong>4.2. No Guarantee.</strong> Actual system performance, savings, and pricing may vary significantly due
          to factors beyond Aurora’s control (including weather, shading, equipment performance, utility rates and
          policies, and Your actual consumption), and estimates are based in part on information You self-report; if
          that information is inaccurate or incomplete, the estimate will change. AURORA DOES NOT GUARANTEE SPECIFIC
          SAVINGS OR ANY REDUCTION IN YOUR UTILITY BILLS. ANY FINANCING-RELATED FIGURES ARE ILLUSTRATIVE, SET BY THIRD
          PARTIES, SUBJECT TO CREDIT APPROVAL, AND NOT AN OFFER OF CREDIT OR A GUARANTEE OF TERMS. Where required by
          applicable law, pricing or financing information may be presented only after You receive and acknowledge
          legally required disclosures for Your jurisdiction. Aurora may facilitate or manage changes to Conceptual
          Information and may cease its involvement in the design process at any point or, if a contract with an
          Installer has been signed, pass the design process entirely to the Installer.
        </p>

        <h3>5. FINANCING</h3>
        <p>
          If You choose to finance Your system, You acknowledge that: (a) financing is subject to the financing
          provider’s own terms, conditions, and approval; (b) it is separate from Aurora’s Services and from Your
          agreement with the Installer; (c) Aurora has no control over or involvement in Your financing outcome, rates,
          or terms; (d) You are solely responsible for reviewing and complying with all financing terms; (e) Aurora
          makes no representations or warranties regarding any financing option; and (f) Aurora is not a lender, loan
          broker, or credit services organization, does not offer, arrange, promote, or recommend financing, and You are
          free to choose Your own lender or financing provider.
        </p>

        <h3>6. ACKNOWLEDGEMENTS</h3>
        <p>
          Changes in laws, regulations, utility policies, or tax incentives are beyond Aurora’s control and may affect
          the financial benefits or operation of the system. You have had the opportunity to seek independent legal
          counsel before accepting these Terms and before entering into any agreement with an Installer.
        </p>

        <h3>7. NO WARRANTY</h3>
        <p>
          THE SERVICES AND ALL INFORMATION, CONTENT, AND MATERIALS (INCLUDING CONCEPTUAL INFORMATION) ARE PROVIDED “AS
          IS” AND “AS AVAILABLE.” AURORA DISCLAIMS ALL WARRANTIES OF ANY KIND, EXPRESS, IMPLIED, OR STATUTORY, INCLUDING
          MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE, TITLE, AND NON-INFRINGEMENT, AND ANY WARRANTIES ARISING OUT
          OF COURSE OF DEALING, USAGE, OR TRADE, AND MAKES NO WARRANTY THAT THE SERVICES WILL BE UNINTERRUPTED, TIMELY,
          SECURE, ERROR-FREE, OR ACCURATE, THAT THE SERVICES WILL MEET YOUR REQUIREMENTS, THAT ANY ERRORS IN THE
          SERVICES WILL BE CORRECTED, OR THAT ANY PRODUCTS, SERVICES, OR INFORMATION OBTAINED THROUGH THE SERVICES OR
          FROM AN INSTALLER WILL MEET YOUR EXPECTATIONS. AURORA SPECIFICALLY DISCLAIMS ANY RESPONSIBILITY FOR THE
          INSTALLATION, FUNCTIONALITY, PERFORMANCE, SAFETY, OR DEFECTS OF ANY SOLAR ENERGY SYSTEM INSTALLED BY AN
          INSTALLER, AND FOR ANY ACTS OR OMISSIONS OF ANY INSTALLER.
        </p>

        <h3>8. LIMITATION OF LIABILITY</h3>

        <p>
          <strong>8.1.</strong> TO THE FULLEST EXTENT PERMITTED BY LAW, AURORA AND ITS AFFILIATES, OFFICERS, DIRECTORS,
          EMPLOYEES, AGENTS, SUPPLIERS, AND LICENSORS (THE “AURORA PARTIES”) SHALL NOT BE LIABLE FOR ANY INDIRECT,
          INCIDENTAL, SPECIAL, CONSEQUENTIAL, EXEMPLARY, OR PUNITIVE DAMAGES (INCLUDING, WITHOUT LIMITATION, DAMAGES FOR
          LOSS OF PROFITS, DATA, USE, REVENUE, GOODWILL, OR OTHER INTANGIBLE LOSSES) ARISING OUT OF OR RELATING TO: THE
          SERVICES OR YOUR INABILITY TO USE THEM; ANY CONDUCT OR OMISSION OF ANY INSTALLER OR OTHER THIRD PARTY
          (INCLUDING POOR WORKMANSHIP, BREACH OF CONTRACT, NEGLIGENCE, FRAUD, MISREPRESENTATION, PROPERTY DAMAGE,
          PERSONAL INJURY, OR DEATH); ANY DISPUTE BETWEEN YOU AND ANY INSTALLER; ANY PRODUCTS, SERVICES, OR INFORMATION
          PROCURED FROM AN INSTALLER; THE CONCEPTUAL INFORMATION; ANY UNAUTHORIZED ACCESS TO YOUR DATA; THE COST OF
          PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES; OR ANY OTHER MATTER RELATING TO THE SERVICES OR YOUR INTERACTIONS
          FACILITATED THEREBY. THIS LIMITATION APPLIES WHETHER LIABILITY IS BASED ON WARRANTY, CONTRACT, TORT (INCLUDING
          NEGLIGENCE), STATUTE, OR ANY OTHER THEORY, EVEN IF AN AURORA PARTY HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH
          DAMAGES AND EVEN IF A REMEDY FAILS OF ITS ESSENTIAL PURPOSE.
        </p>

        <p>
          <strong>8.2.</strong> ANY CLAIM RELATED TO THE INSTALLATION, PERFORMANCE, WARRANTY, OR SERVICING OF YOUR SOLAR
          ENERGY SYSTEM MUST BE DIRECTED SOLELY TO THE INSTALLER. AURORA IS NOT INVOLVED IN ANY WAY WITH THE ACTUAL
          INSTALLATION, FINANCING (BEYOND DISPLAYING THIRD-PARTY ESTIMATES, IF APPLICABLE), TROUBLESHOOTING, TECHNICAL
          SUPPORT, OR WARRANTY CLAIMS FOR THE SOLAR ENERGY SYSTEM ITSELF.
        </p>

        <p>
          <strong>8.3.</strong> THE AURORA PARTIES’ TOTAL AGGREGATE LIABILITY FOR ALL CLAIMS ARISING OUT OF OR RELATING
          TO THESE Terms OR THE SERVICES SHALL NOT EXCEED ONE HUNDRED UNITED STATES DOLLARS ($100.00 USD).
        </p>

        <h3>9. INDEMNIFICATION</h3>
        <p>
          You agree to indemnify, defend, and hold harmless the Aurora Parties from and against all claims, liabilities,
          damages, losses, costs, and expenses (including reasonable attorneys’ fees and court costs) arising out of or
          relating to: Your use of the Services; Your violation of these Terms, applicable law, or any third-party
          rights; Your interactions, agreements, or disputes with any Installer; any content You submit through the
          Services; or Your misuse of or reliance on Conceptual Information contrary to these Terms. Aurora may assume
          the exclusive defense and control of any such matter at its own expense, and You will cooperate in asserting
          available defenses.
        </p>

        <h3>10. USER CONDUCT</h3>
        <p>
          You will not use the Services for any unlawful purpose or in any way that could damage, disable, or impair the
          Services or interfere with any other party’s use, including by transmitting objectionable material or
          malicious code, infringing intellectual property rights, or attempting unauthorized access to connected
          systems. You are solely responsible for all information and content You provide through the Services.
        </p>

        <h3>11. TERM AND TERMINATION</h3>
        <p>
          Aurora may suspend, modify, or terminate Your access to all or part of the Services at any time, with or
          without cause or notice, effective immediately. Upon termination, Your right to use the Services will cease
          immediately. Provisions that by their nature should survive termination (including ownership provisions,
          warranty disclaimers, indemnity, and limitations of liability) survive.
        </p>

        <h3>12. INTELLECTUAL PROPERTY</h3>
        <p>
          The Services and all intellectual property rights therein, including derivative works, are and will remain the
          sole and exclusive property of Aurora Solar Inc. The Services are licensed, not sold, and these Terms convey
          no ownership rights in the Services.
        </p>

        <h3>13. YOUR DATA; PRIVACY</h3>
        <p>
          Aurora’s collection, use, disclosure, and retention of Your personal information are governed by our{' '}
          <Link href={privacyPolicyUrl}>Privacy Policy</Link>, which also describes Your privacy choices and rights,
          including opt-out rights where applicable. You grant Aurora a non-exclusive, transferable, sub-licensable,
          royalty-free, fully paid up, perpetual, irrevocable, worldwide license to use, reproduce, make available,
          sell, share, publish, translate, modify, create derivative works from, and distribute the information You
          submit through the Services (“Your Data”) in connection with providing and improving the Services and for
          Aurora’s other legitimate business interests, as restricted by the{' '}
          <Link href={privacyPolicyUrl}>Privacy Policy</Link>. You consent to Aurora sharing Your Data (including
          Service Check results and other information You provide) with the Installer(s) with whom You are connected or
          express interest, or other Installers serving Your area, as described at the point of collection, so that they
          may provide You with estimates, proposals, and services. Once Your Data is transferred to an Installer, the
          Installer processes it independently under its own privacy policy and terms, and Aurora is not responsible for
          the Installer’s subsequent use of Your Data. Aurora may retain a copy of Your signed installation contract,
          receive project status information from Installers, use aggregated or de-identified information for its
          business purposes, and use third-party service providers as described in the{' '}
          <Link href={privacyPolicyUrl}>Privacy Policy</Link>. By using the Services, You consent to the processing and
          collection of Your Data by such third-party service providers. You retain ownership of Your Data and, where
          applicable and permitted by law, waive any moral rights in it.
        </p>

        <h3>14. COMMUNICATIONS</h3>
        <p>
          By using the Services, You consent to receive Service-related communications and, if You opt in, promotional
          communications from Aurora by email, text message, and phone. CONSENT TO MARKETING CALLS OR TEXTS IS NOT A
          CONDITION OF PURCHASE. You may opt out at any time by following the instructions in the communication
          (including replying STOP to texts), through the mechanisms described in our{' '}
          <Link href={privacyPolicyUrl}>Privacy Policy</Link>, or by emailing{' '}
          <a href="mailto:privacy@aurorasolar.com">privacy@aurorasolar.com</a>. Message and data rates may apply;
          message frequency varies. We will comply with the Telephone Consumer Protection Act (TCPA). Communications
          made through the platform may be monitored, stored, and reviewed for service quality, support, safety, and
          compliance purposes.
        </p>

        <h3>15. GOVERNING LAW; DISPUTE RESOLUTION</h3>
        <p>
          These Terms are governed by the laws of the State of California, without regard to conflict of law provisions.
          Any controversy or claim arising out of or relating to these Terms shall be resolved by binding individual
          arbitration administered by the American Arbitration Association under its Consumer Arbitration Rules, in the
          county where You reside or another mutually agreed location (including by telephone or videoconference);
          judgment on the award may be entered in any court of competent jurisdiction. Either party may bring an
          individual action in small claims court, or seek equitable relief for infringement of intellectual property
          rights in a court of competent jurisdiction in San Francisco County, California. YOU AND AURORA EACH AGREE TO
          BRING CLAIMS ONLY IN AN INDIVIDUAL CAPACITY AND NOT AS A PLAINTIFF OR CLASS MEMBER IN ANY CLASS OR
          REPRESENTATIVE PROCEEDING. Nothing in this Section waives any right to seek public injunctive relief where
          such a waiver is prohibited by applicable law.
        </p>

        <h3>16. GENERAL</h3>
        <p>
          These Terms, together with the <Link href={privacyPolicyUrl}>Privacy Policy</Link>, are the entire agreement
          between You and Aurora regarding Your use of the Aurora Marketplace Services as a homeowner and supersede all
          prior communications and understandings. NO VERBAL AGREEMENTS OR UNDERSTANDINGS NOT EXPLICITLY SET FORTH
          HEREIN SHALL BE BINDING. Aurora may modify these Terms at any time and will provide notice of material changes
          by posting or other reasonable means; Your continued use constitutes acceptance. It is Your responsibility to
          check these Terms periodically for changes. Any pricing or estimates presented via the Services are based on
          Installer-provided information, information You provide, and other data sources; Aurora does not determine or
          guarantee final pricing. AURORA RECEIVES COMPENSATION FROM INSTALLERS FOR THE SERVICES, which may include
          platform, referral, and other fees (including, for Managed Sales Installers, fees for sales support provided
          on the Installer’s behalf); these fees are paid by the Installer and do not directly increase the cost of Your
          system, although they may be factored into the Installer’s overall pricing. Aurora has no financial
          involvement in the installation contract between You and the Installer, including payments, deposits, or
          milestone payments. If any provision is held invalid or unenforceable, it will be limited or eliminated to the
          minimum extent necessary and the remaining provisions remain in full force. You may not assign these Terms
          without Aurora’s prior written consent; Aurora may assign at any time without notice. These Terms inure to the
          benefit of, and bind, the parties and their respective successors and permitted assigns. Notices to Aurora
          must be in writing to the address listed on our website or a designated legal email; notices to You may be
          sent to the email You provided. No waiver of any provision is a further or continuing waiver. Headings are for
          convenience only.
        </p>
      </DS.Flex>
    </>
  );
};
